// 入札一覧の CSV ダウンロードを押せるか、 押せる時に
// 排出係数紐付けに使う自社の事業者 ID を、 手数料自動考慮の設定・手数料単価・会社情報の取得状態から
// 決める (`bidding/spot-bidding.md` §2 機能詳細 9、 AC-BSP-100)。
//
// 価格列は、 手数料自動考慮が ON の間は保存済みの入札価格から現在の手数料単価で逆算した
// 希望価格を出す (取り込むと同じ入札価格に戻るため)。 ON なのに手数料単価が無いと、 入札価格を
// そのまま出してもデータを誤るので、 押せなくして理由を出す (AC-BSP-100)。 価格列の値そのものは
// 押した後のプレビュー応答で決める ([[useBidCsvExportContext]])。 押せない理由の文 (会員 ID 未登録の
// 出し分けを含む) はプレビュー応答の状態だけでは分けられないので、 押す前の判定はここに残す。
//
// spec が出力を止める条件は AC-BSP-100 だけなので、 それ以外の前提 (手数料自動考慮の設定と
// 会社情報) は、 取得している間は止めずに待つ (押された時に届くのを待ってから出す。
// [[useBidCsvExportContext]])。 取得に失敗した時だけ止める。 設定が分からないまま OFF として
// 出すと、 ON の組織では入札価格がそのまま入り、 取り込むと手数料が二重にかかる。 会社情報が
// 分からないまま出すと、 排出係数紐付けの札を `1` + 英字 1 文字に戻せず、 再アップロードで
// 紐付けが外れる。 この 2 つの理由の文は spec に無い仮の文言 (PdM・Des への確認は未実施)。
import {
  type BidCsvPriceQuery,
  type BidCsvPriceSource,
  bidCsvPriceQueryKey,
} from '~/pages/SpotBidding/models/bidCsvCells'

/**
 * 出せない理由 (押せない理由の文を分ける)。
 *
 * - `feeUnitUnavailable`: 手数料自動考慮が ON で、 手数料単価を取得できていない (AC-BSP-100)。
 *   待てば取得できるので「時間をおいて再度お試しください」と案内する
 * - `memberUnregistered`: 手数料自動考慮が ON で、 JEPX 会員 ID が未登録の組織 (AC-BSP-100)。
 *   待っても手数料単価は現れず、 登録は運営が行うので、 運営への案内にする
 * - `feeAutoConsiderUnavailable`: 手数料自動考慮の設定 (ON / OFF) の取得に失敗した (仮の文言)
 * - `companyInfoUnavailable`: 会社情報 (自社の事業者 ID) の取得に失敗した (仮の文言)
 */
export type BidCsvExportBlockedReason =
  | 'feeUnitUnavailable'
  | 'memberUnregistered'
  | 'feeAutoConsiderUnavailable'
  | 'companyInfoUnavailable'

/**
 * CSV の組み立てに渡す、 出力の前提 (価格列の出所と自社の事業者 ID)。 押す前の判定が `ready` で、
 * 押した後のプレビュー応答が出せる状態の時にだけ得られる ([[useBidCsvExportContext]])。
 */
export type BidCsvExportInputs = {
  price: BidCsvPriceSource
  /**
   * 自社の登録小売電気事業者 ID。 排出係数紐付けの札を判別コメントから `1` + 英字 1 文字へ
   * 戻すのに使う。 会社情報が未登録の時は undefined (判別コメントを認識せず、 保存済みの
   * JEPX自由記述欄をそのまま出す。 内容は失わず、 取り込み直しても同じ note に戻る)。
   */
  retailerId: string | undefined
}

/**
 * - `ready`: 出せる
 * - `waiting`: 手数料自動考慮の設定か会社情報を取得している。 押せるままにし、 押されたら
 *   届くのを待ってから判定し直す
 * - `blocked`: 出せない。 押せなくして理由を文で出す
 */
export type BidCsvExportGate =
  | { kind: 'ready'; retailerId: string | undefined }
  | { kind: 'waiting' }
  | { kind: 'blocked'; reason: BidCsvExportBlockedReason }

export const resolveBidCsvExportGate = ({
  bidLimitsLoading,
  bidLimitsError,
  feeAutoConsiderEnabled,
  feeStatus,
  feeYenPerKwh,
  memberRegistered,
  companyInfoLoading,
  companyInfoError,
  retailerId,
}: {
  /** 手数料自動考慮の設定 (入札制限) を取得しているか。 */
  bidLimitsLoading: boolean
  /** 手数料自動考慮の設定 (入札制限) の取得に失敗したか。 */
  bidLimitsError: boolean
  /** 手数料自動考慮が ON か。 設定を取得するまでは意味を持たない。 */
  feeAutoConsiderEnabled: boolean
  feeStatus: 'loading' | 'error' | 'success'
  feeYenPerKwh: number | undefined
  /** JEPX 会員 ID を登録済みか。 手数料単価の取得が成功するまでは undefined。 */
  memberRegistered: boolean | undefined
  /** 会社情報 (自社の事業者 ID) を取得しているか。 */
  companyInfoLoading: boolean
  /** 会社情報 (自社の事業者 ID) の取得に失敗したか。 */
  companyInfoError: boolean
  /** 自社の登録小売電気事業者 ID。 未登録・取得前・取得失敗は undefined。 */
  retailerId: string | undefined
}): BidCsvExportGate => {
  // 取得の失敗は待っても変わらないので、 他の前提を待たずに止める。
  if (bidLimitsError) {
    return { kind: 'blocked', reason: 'feeAutoConsiderUnavailable' }
  }
  if (companyInfoError) {
    return { kind: 'blocked', reason: 'companyInfoUnavailable' }
  }
  // ON か OFF かが分かるまでは、 手数料単価が要るかを決められないので待つ。
  if (bidLimitsLoading) {
    return { kind: 'waiting' }
  }
  // AC-BSP-100: 手数料自動考慮が ON で、 使える手数料単価が無い間 (取得中・取得失敗・単価が
  // 空や 0.01 円/kWh の整数倍でない) は止める。 単価が使えるかは、 作成モーダルの登録判定と同じ
  // [[resolveFeeAutoConsiderReadiness]] で決める (条件が増えた時に片方だけを直さないように)。
  const feeReadiness = resolveFeeAutoConsiderReadiness({
    bidLimitsLoading,
    bidLimitsError,
    feeAutoConsiderEnabled,
    feeLoading: feeStatus === 'loading',
    feeError: feeStatus === 'error',
    feeYenPerKwh,
  })
  if (feeReadiness !== 'ready') {
    return {
      kind: 'blocked',
      // 会員 ID が未登録と分かるのは、 手数料単価の取得が成功した後だけ。
      reason:
        memberRegistered === false
          ? 'memberUnregistered'
          : 'feeUnitUnavailable',
    }
  }
  if (companyInfoLoading) {
    return { kind: 'waiting' }
  }
  return { kind: 'ready', retailerId }
}

/**
 * 手数料自動考慮の ON/OFF 設定・手数料単価の取得状況から、 手数料単価が要るなら使える状態かを解決する。
 *
 * - `'loading'`: 設定 (入札制限、 ON/OFF を含む) の取得中、 または ON 確定後の手数料単価の取得中。
 * - `'unavailable'`: 設定の取得に失敗した (= ON/OFF が未確定のまま)、 または
 *   ON 確定後の手数料単価の取得に失敗した。
 * - `'ready'`: OFF と確定していれば手数料単価を待たずに、 ON と確定していれば
 *   手数料単価も取得済みなら。
 *
 * 取得中・取得失敗中の `feeAutoConsiderEnabled` は `false` へ倒れる (`useBidLimits`) ので、
 * 設定の取得状態を先に見る。 取得は成功したが単価が空・非数値 (`useSpotFeeYenPerKwh` の parse 失敗) の
 * 間も、 調整に使える単価が無いので `'unavailable'` に倒す。
 */
const resolveFeeAutoConsiderReadiness = ({
  bidLimitsLoading,
  bidLimitsError,
  feeAutoConsiderEnabled,
  feeLoading,
  feeError,
  feeYenPerKwh,
}: {
  bidLimitsLoading: boolean
  bidLimitsError: boolean
  feeAutoConsiderEnabled: boolean
  feeLoading: boolean
  feeError: boolean
  feeYenPerKwh: number | undefined
}): 'loading' | 'unavailable' | 'ready' => {
  if (bidLimitsLoading) return 'loading'
  if (bidLimitsError) return 'unavailable'
  if (!feeAutoConsiderEnabled) return 'ready'
  if (feeLoading) return 'loading'
  if (feeError) return 'unavailable'
  if (feeYenPerKwh === undefined) return 'unavailable'
  return 'ready'
}

/** プレビュー応答のうち、 価格列の出所を決めるのに読む部分 (分割した問い合わせ 1 回ぶん)。 */
export type BidCsvPriceResponse = {
  feeAutoConsider?: { enabled: boolean; unavailable: boolean }
  desiredPriceResults: ReadonlyArray<{
    desiredPriceYenPerKwh?: { value: string }
  }>
}

export type BidCsvPriceOutcome =
  | { kind: 'price'; price: BidCsvPriceSource }
  | {
      kind: 'failed'
      reason: Extract<
        BidCsvExportBlockedReason,
        'feeUnitUnavailable' | 'feeAutoConsiderUnavailable'
      >
    }

/**
 * プレビュー応答 (分割した問い合わせごと。 `responses[i]` が `chunks[i]` の応答) から価格列の出所を決める
 * (AC-BSP-004b)。 押す前の判定 (gate) が OFF と見ていても応答が ON なら逆算値を使う (押してから応答までに
 * ON に変わった組織へ入札価格を出すと、 取り込み直した時に手数料が二重にかかる)。 応答どうしで状態が
 * 食い違う時は、 どちらの価格列も正しいと言えないので出さない。
 */
export const resolveBidCsvPriceSource = (
  chunks: ReadonlyArray<readonly BidCsvPriceQuery[]>,
  responses: readonly BidCsvPriceResponse[],
): BidCsvPriceOutcome => {
  const states = responses.map((r) => r.feeAutoConsider)
  const [first] = states
  if (
    first === undefined ||
    states.some(
      (s) =>
        s === undefined ||
        s.enabled !== first.enabled ||
        s.unavailable !== first.unavailable,
    )
  ) {
    return { kind: 'failed', reason: 'feeAutoConsiderUnavailable' }
  }
  if (!first.enabled) return { kind: 'price', price: { kind: 'orderPrice' } }
  if (first.unavailable) {
    return { kind: 'failed', reason: 'feeUnitUnavailable' }
  }
  const desiredByQuery = new Map<string, string>()
  for (const [i, chunk] of chunks.entries()) {
    for (const [j, query] of chunk.entries()) {
      const desired =
        responses[i]?.desiredPriceResults[j]?.desiredPriceYenPerKwh?.value
      if (desired === undefined) {
        return { kind: 'failed', reason: 'feeUnitUnavailable' }
      }
      desiredByQuery.set(bidCsvPriceQueryKey(query), desired)
    }
  }
  return {
    kind: 'price',
    price: {
      kind: 'desiredPrice',
      desiredPriceYenPerKwhByQuery: desiredByQuery,
    },
  }
}
